import { ApiError, TransportError } from "@tdelivr/api-client";

import type { DriverApi } from "./driver-api.ts";
import type { ChipClass } from "./shift-readiness.ts";

/**
 * `6 Offline queue` — the work this phone is holding, and how it leaves.
 *
 * The design's own sentence is the specification: _"Scans and proof are saved on this phone and sent
 * in order."_ So the subject of this screen is **device state**, not a platform read. Nothing on the
 * platform publishes "what this handset has not managed to send yet" and nothing could: the whole
 * point of the queue is that the platform has not heard about any of it.
 *
 * That makes this module the one place in the application that owns a store rather than a request,
 * and it keeps three things apart on purpose:
 *
 *   - the **record** of a queued piece of work, which is persisted and survives a reload;
 *   - the **bytes** of a captured file, which are held separately because a photograph does not go
 *     in `localStorage`;
 *   - the **sending**, which is ordered, stops at the first thing that did not go, and is the only
 *     part that talks to the platform.
 *
 * ## Where the two destinations come from, and why they are not EXP-003's
 *
 * A queued scan goes to **LOG-06**, `POST /v1/device/scans`. A queued file goes to **FIL-02**,
 * `POST /v1/tenants/{tenantId}/uploads` and its two companions. Both are commands, and this
 * application's settled rule — `src/driver-api.ts`, ADR 0024 — is that reads go to EXP-003 and
 * commands go to the service that owns them. EXP-003 publishes three operations and all three are
 * reads, so there is nothing here to route through it.
 *
 * FIL-02 is reachable at all because of [ADR 0025](../../../docs/adr/0025-the-upload-service-gets-a-public-surface-and-its-own-device-scope.md),
 * which published its three operations on `/v1` behind one new scope, `uploads:self`, and bound each
 * upload to the token subject that initiated it. Two consequences of that ADR are load-bearing here:
 *
 *   - **An upload this subject did not initiate answers 404, not 403.** So a 404 on a resumed upload
 *     is not "it went away", it is "it is not yours", and the queue starts that item again rather
 *     than reporting a failure a courier cannot act on.
 *   - **The initiating subject is part of the idempotency fingerprint.** So the key below is per
 *     queued item and is stable across retries — one key per logical act, never one per press.
 *
 * ## Duplicates, staleness and the gap, which are the design's own words
 *
 * _"Duplicates are fine and ignored. Only a gap in the sequence is a problem, and we'll tell you."_
 * That is LOG-06's published contract rather than a reassurance: its 200 carries
 * `outcome: recorded | duplicate | stale` and its 409 is a device source-sequence gap. So a retry of
 * a scan needs no idempotency key — the scan's own `scanId` and `sourceSequence` are the
 * deduplication — and a 409 is the one outcome that stops the queue and is reported.
 */

/* ------------------------------------------------------------------------- what a queue holds */

/** LOG-06's `type` enum, exactly. */
export type ScanType =
  "picked_up" | "hub_arrival" | "hub_departure" | "out_for_delivery" | "delivered" | "exception";

/** LOG-06's `location`, which is one of two shapes and not a free object. */
export type ScanLocation =
  | { readonly kind: "hub"; readonly hubId: string }
  | { readonly kind: "geo"; readonly latitude: number; readonly longitude: number };

/** FIL-02's allow-list, which is a domain rule and the same on both of its surfaces. */
export type UploadContentType = "image/jpeg" | "image/png" | "application/pdf" | "text/plain";

/** A scan captured on this phone and not yet acknowledged by LOG-06. */
export interface QueuedScan {
  readonly kind: "scan";
  /** This device's own identifier for the queue entry. Not a platform identifier. */
  readonly id: string;
  /** When this phone saved it, which is what the design's `saved 10:52` prints. */
  readonly savedAt: string;
  readonly scanId: string;
  readonly shipmentId: string;
  readonly parcelId?: string;
  readonly sourceSequence: number;
  readonly type: ScanType;
  readonly location: ScanLocation;
  readonly occurredAt: string;
}

/** A file captured on this phone and not yet in FIL-02's quarantine. */
export interface QueuedUpload {
  readonly kind: "upload";
  readonly id: string;
  readonly savedAt: string;
  /**
   * What this phone recorded the capture as being of — the design's `door`.
   *
   * This application's own field and not a platform one. FIL-02 takes a content type, a size and a
   * digest and nothing about the subject, and DEL-07's evidence carries a `kind` rather than a
   * caption. So the courier's own word for what they photographed is kept here, where the capture
   * happened, rather than being asked of a service that was never told it.
   */
  readonly subject: string;
  readonly contentType: UploadContentType;
  readonly expectedSizeBytes: number;
  /** Lowercase hexadecimal, which is what FIL-02's `expectedSha256` pattern requires. */
  readonly expectedSha256: string;
  /** Set once FIL-02 has minted the upload, so a resumed attempt completes rather than re-initiating. */
  readonly uploadId?: string;
  /** The aggregate version `complete` has to match. Published by the initiation. */
  readonly version?: number;
  /** Set once the bytes have reached the object store, so a resumed attempt does not write twice. */
  readonly written?: boolean;
}

export type QueuedItem = QueuedScan | QueuedUpload;

/**
 * Where the queue lives.
 *
 * An interface rather than `localStorage` directly, for two reasons that are both about honesty. A
 * test can hold a queue in memory and assert the order things left in; and the bytes of a photograph
 * genuinely cannot be stored beside the record — `localStorage` holds strings — so the two are
 * separated here rather than pretended to be one thing.
 */
export interface QueueStore {
  list(): readonly QueuedItem[];
  replace(items: readonly QueuedItem[]): void;
  /** The captured bytes, when this session is still holding them. */
  bytes(id: string): Blob | undefined;
}

/** The key the browser store writes under. Named so it can be found in a devtools session. */
export const QUEUE_STORAGE_KEY = "tdelivr.driver-app.offline-queue";

/**
 * The browser's own store: the records in `localStorage`, the bytes in memory.
 *
 * The split is a real limitation of a web application rather than a shortcut, and it is drawn rather
 * than hidden: a queued file whose bytes this session does not hold is shown as held and is not
 * sent, because sending a record with no bytes would create an upload nothing could ever write to.
 * Recorded as Q-73.
 */
export function browserQueueStore(
  storage: Pick<Storage, "getItem" | "setItem"> = window.localStorage,
  captured: Map<string, Blob> = new Map(),
): QueueStore {
  return {
    list(): readonly QueuedItem[] {
      /*
       * A store that cannot be read is an empty queue rather than a thrown error.
       *
       * `localStorage` throws on access in a browser with site data blocked, and a courier whose
       * phone is in that state has no queue — which is a true statement — rather than an
       * application that will not start.
       */
      let raw: string | null = null;
      try {
        raw = storage.getItem(QUEUE_STORAGE_KEY);
      } catch {
        return [];
      }
      if (raw === null) return [];
      try {
        const parsed = JSON.parse(raw) as unknown;
        return Array.isArray(parsed) ? parsed.filter(isQueuedItem) : [];
      } catch {
        return [];
      }
    },
    replace(items: readonly QueuedItem[]): void {
      try {
        storage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(items));
      } catch {
        /*
         * Nothing to do and nothing to say to a courier.
         *
         * The queue in memory is still correct for this session; what is lost is that it survives a
         * reload, and a message about storage quotas on a delivery screen helps nobody.
         */
      }
    },
    bytes(id: string): Blob | undefined {
      return captured.get(id);
    },
  };
}

/**
 * Whether a parsed record is one of the two shapes, so a stale store cannot produce a half item.
 *
 * Read off an index signature rather than off `Partial<QueuedScan & QueuedUpload>`: the two shapes
 * have different literal `kind`s, so their intersection makes that field `never` and every narrowing
 * below it becomes an error. The point of this function is that the value came out of storage and
 * has no type yet.
 */
function isQueuedItem(value: unknown): value is QueuedItem {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Readonly<Record<string, unknown>>;
  if (typeof row.id !== "string" || typeof row.savedAt !== "string") return false;
  if (row.kind === "scan") {
    return (
      typeof row.scanId === "string" &&
      typeof row.shipmentId === "string" &&
      typeof row.sourceSequence === "number" &&
      typeof row.type === "string" &&
      typeof row.occurredAt === "string" &&
      typeof row.location === "object" &&
      row.location !== null
    );
  }
  if (row.kind === "upload") {
    return (
      typeof row.subject === "string" &&
      typeof row.contentType === "string" &&
      typeof row.expectedSizeBytes === "number" &&
      typeof row.expectedSha256 === "string"
    );
  }
  return false;
}

/* ------------------------------------------------------------------------------- what is drawn */

/**
 * The design's three row labels, derived from the record rather than written out.
 *
 * `Scan 1147`, `Photo, door` and `Exception note` are the design's own three samples, and each is a
 * sentence about one queued record:
 *
 *   - a scan prints `Scan ` and its own `sourceSequence`, which is the number the platform orders
 *     scans by and therefore the number a courier would be asked about if there were a gap;
 *   - a scan of type `exception` prints `Exception note`, because the design does, and an exception
 *     is the one scan kind whose sequence is not the useful thing about it;
 *   - a file prints `Photo, ` and this phone's own word for what was captured, or `File, ` when it
 *     is not an image — `7 Document expiring` uploads a permit, which FIL-02 accepts as a PDF.
 */
export function itemLabel(item: QueuedItem): string {
  if (item.kind === "scan") {
    return item.type === "exception" ? "Exception note" : `Scan ${String(item.sourceSequence)}`;
  }
  const noun =
    item.contentType === "image/jpeg" || item.contentType === "image/png" ? "Photo" : "File";
  return `${noun}, ${item.subject}`;
}

/** `saved 10:52` — the design's own value, on this phone's clock. Q-65: no tenant timezone exists. */
export function savedLabel(savedAt: string): string | null {
  const parsed = new Date(savedAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return `saved ${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`;
}

/**
 * How the queue is getting on, which is what the banner and the chip say.
 *
 * `queued` is the design's own state — the amber strip, the dashed `pending sync` pill. The other
 * four are states this screen reaches and the design does not draw, so each is rendered in the
 * design's vocabulary with this build's words, flagged as such. Recorded as Q-59, the same question
 * the four `ViewState` screens are recorded under.
 */
export type QueueCondition =
  /** Nothing is waiting. Everything this phone captured has been acknowledged. */
  | { readonly kind: "empty" }
  /** The design's state: items waiting, and the last attempt did not reach a service. */
  | { readonly kind: "queued" }
  /** An attempt is in flight. */
  | { readonly kind: "sending" }
  /** LOG-06 answered 409: a gap in this device's sequence. The design promises to say so. */
  | { readonly kind: "gap"; readonly label: string }
  /** The first item cannot be sent from this session — its bytes are not here. */
  | { readonly kind: "held"; readonly label: string }
  /** The token does not carry what sending needs. Named, so a courier can ask for it. */
  | { readonly kind: "forbidden"; readonly missingScopes: readonly string[] }
  /** A service refused for a reason that is neither of the above. */
  | { readonly kind: "refused"; readonly code: string; readonly message: string };

/** The amber strip's text. `Offline · 3 items waiting to sync` is the design's, word for word. */
export function bannerFor(
  condition: QueueCondition,
  waiting: number,
): { readonly glyph: string; readonly text: string } | undefined {
  const items = `${String(waiting)} ${waiting === 1 ? "item" : "items"} waiting to sync`;
  switch (condition.kind) {
    case "empty":
      return undefined;
    case "queued":
      return { glyph: "cloud_off", text: `Offline · ${items}` };
    case "sending":
      return { glyph: "cloud_upload", text: `Sending · ${items}` };
    case "gap":
      return { glyph: "error", text: `Sequence gap · ${items}` };
    case "held":
      return { glyph: "cloud_off", text: `Can't send yet · ${items}` };
    case "forbidden":
      return { glyph: "lock", text: `Not permitted · ${items}` };
    case "refused":
      return { glyph: "error", text: `Not accepted · ${items}` };
  }
}

/** The state chip, which shows a glyph and a word and never a colour alone. */
export function chipFor(condition: QueueCondition): {
  readonly class: ChipClass;
  readonly label: string;
} {
  switch (condition.kind) {
    case "empty":
      return { class: "done", label: "synced" };
    case "queued":
      // The design's own: `['unknown','pending sync']`.
      return { class: "unknown", label: "pending sync" };
    case "sending":
      return { class: "waiting", label: "sending" };
    case "gap":
      return { class: "caution", label: "sequence gap" };
    case "held":
      return { class: "unknown", label: "held on phone" };
    case "forbidden":
      return { class: "human", label: "action_required" };
    case "refused":
      return { class: "closed", label: "not accepted" };
  }
}

/**
 * The sheet's note.
 *
 * The design's dashed `pending` card carries one sentence and it is kept word for word on every
 * state but two. A gap is the one thing the design says it will tell the courier about, so that
 * state says which item the gap is at and keeps the design's second sentence; a queue with nothing
 * in it has no sequence to have a gap in.
 */
export function noteFor(condition: QueueCondition): {
  readonly kind: "info" | "pending";
  readonly text: string;
} {
  const designed =
    "Duplicates are fine and ignored. Only a gap in the sequence is a problem, and we'll tell you.";
  switch (condition.kind) {
    case "gap":
      return {
        kind: "pending",
        text: `There's a gap in this phone's sequence at ${condition.label}, so nothing after it can be sent. ${designed}`,
      };
    case "held":
      return {
        kind: "pending",
        text: `${condition.label} was captured in an earlier session and its file is no longer on this phone, so it can't be sent. ${designed}`,
      };
    case "forbidden":
      return {
        kind: "info",
        text: `Your sign-in doesn't carry what sending needs${
          condition.missingScopes.length === 0 ? "" : `: ${condition.missingScopes.join(" and ")}`
        }. Nothing has been lost. ${designed}`,
      };
    case "refused":
      return { kind: "info", text: `${condition.message} (${condition.code}) ${designed}` };
    case "empty":
    case "queued":
    case "sending":
      return { kind: "pending", text: designed };
  }
}

/** Whether this screen's words are the design's, so the flag under the title can say when they are not. */
export function wordingIsDesigned(condition: QueueCondition): boolean {
  return condition.kind === "queued";
}

/* ------------------------------------------------------------------------------- what is sent */

/** What happened to one item on one pass. */
export interface QueueAttempt {
  readonly id: string;
  readonly label: string;
  readonly outcome: "recorded" | "duplicate" | "stale" | "uploaded";
}

export interface QueueSyncReport {
  /** The items the platform has now acknowledged, in the order they were sent. */
  readonly accepted: readonly QueueAttempt[];
  /** What the queue should now hold, records updated where an upload got partway. */
  readonly remaining: readonly QueuedItem[];
  /** Why the pass stopped, when it stopped before the end. */
  readonly condition: QueueCondition;
}

/** The scopes sending needs, named so a refusal can say which one is absent. */
export const SCAN_SCOPE = "scan:device-ingest";
export const UPLOAD_SCOPE = "uploads:self";

/**
 * Send what is waiting, in order, and stop at the first thing that did not go.
 *
 * **In order, and stopping.** The design says _"sent in order"_ and LOG-06 enforces it: a scan whose
 * `sourceSequence` leaves a hole is refused 409. Carrying on past a failure would send a scan the
 * platform will refuse and would put a later photograph in quarantine against a delivery whose
 * earlier scan never arrived.
 *
 * **One key per logical act.** A queued upload's three calls carry `upload-<id>` and
 * `complete-<id>`, derived from the queue entry rather than minted per attempt, so a retry over a
 * connection that dropped mid-request is the same upload rather than a second object. ADR 0025
 * section 4 put the initiating subject in FIL-02's fingerprint, so this key is also safe against
 * another courier replaying it: they are told the key was used differently rather than handed this
 * courier's write grant.
 */
/**
 * The scope DRV-03's write needs, and the one document kind the design draws.
 *
 * `permit` is copied rather than chosen. DRV-03's `type` is a free string with no published
 * vocabulary, and the design names exactly one document on this flow and names it twice: the
 * upload screen's headline "Uploading your permit", and `1b Can't go online`'s "Your PrDP has
 * expired — you can't go online until a valid permit is approved". A picker offering several
 * kinds would be a control the design does not draw. When it draws a second kind, the type comes
 * from the capture instead of from here.
 */
const ATTACH_SCOPE = "driver-document:write";
const DESIGNED_DOCUMENT_TYPE = "permit";

export async function sendQueue(input: {
  readonly items: readonly QueuedItem[];
  readonly api: DriverApi;
  readonly tenantId: string;
  readonly bytes: (id: string) => Blob | undefined;
  /** The scopes the token carries, so an attempt that cannot succeed is not made. */
  readonly scopes: ReadonlySet<string>;
  /**
   * The courier this queue belongs to, where the token says so.
   *
   * Undefined when the realm issues no `driver_id` claim, and then a completed upload is not
   * attached to a DRV-03 document — the bytes are safely in the object store and nothing
   * claims which courier's permit they are. Guessing from the subject is what `session.ts`
   * refuses for every other read on R-29's grounds, and a document written against the wrong
   * `driverId` would be worse here than on a read: it would be a permanent record about
   * somebody else.
   */
  readonly driverId?: string | undefined;
}): Promise<QueueSyncReport> {
  const accepted: QueueAttempt[] = [];
  const items = [...input.items];

  while (items.length > 0) {
    const item = items[0]!;
    const label = itemLabel(item);

    const needed = item.kind === "scan" ? SCAN_SCOPE : UPLOAD_SCOPE;
    if (!input.scopes.has(needed)) {
      return {
        accepted,
        remaining: items,
        condition: { kind: "forbidden", missingScopes: [needed] },
      };
    }

    if (item.kind === "scan") {
      try {
        const outcome = await input.api.ingestScan({
          scanId: item.scanId,
          shipmentId: item.shipmentId,
          ...(item.parcelId === undefined ? {} : { parcelId: item.parcelId }),
          sourceSequence: item.sourceSequence,
          type: item.type,
          location: item.location,
          occurredAt: item.occurredAt,
        });
        /*
         * `undefined` is `recorded`, and that is the contract rather than a guess.
         *
         * LOG-06's 201 is "Immutable scan recorded" and publishes no response body at all; only its
         * 200 — the duplicate or stale acknowledgement — carries `outcome`. So an empty answer is
         * the first-time case, and defaulting it to `duplicate` would tell a courier their scan was
         * already known the one time it was not.
         */
        accepted.push({ id: item.id, label, outcome: outcome ?? "recorded" });
        items.shift();
        continue;
      } catch (error: unknown) {
        return { accepted, remaining: items, condition: conditionFor(error, label, needed) };
      }
    }

    const sent = await sendUpload({
      item,
      api: input.api,
      tenantId: input.tenantId,
      bytes: input.bytes,
      ...(input.driverId === undefined ? {} : { driverId: input.driverId }),
      attachable: input.scopes.has(ATTACH_SCOPE),
    });
    /*
     * The record is written back **before** the failure is read, and that ordering is the whole
     * point of `sendUpload` returning its error rather than throwing it.
     *
     * An upload that was minted and written and then lost the connection on `complete` has made
     * real progress: it holds FIL-02's `uploadId` and its version, and the object is in the store.
     * Discarding that — which an exception propagating past here did, and a test caught — would make
     * the next pass initiate again and leave the first object in quarantine forever.
     */
    items[0] = sent.item;
    if (sent.held === true) {
      return { accepted, remaining: items, condition: { kind: "held", label } };
    }
    if (sent.error !== undefined) {
      return { accepted, remaining: items, condition: conditionFor(sent.error, label, needed) };
    }
    if (!sent.complete) {
      return { accepted, remaining: items, condition: { kind: "queued" } };
    }
    accepted.push({ id: item.id, label, outcome: "uploaded" });
    items.shift();
  }

  return { accepted, remaining: [], condition: { kind: "empty" } };
}

/**
 * One queued file, through FIL-02's three operations and the object store in the middle.
 *
 * The middle step is not a platform request and must not be made like one: the bytes go to
 * `grant.uploadUrl` with `grant.requiredHeaders` and **no bearer token**, because the grant is the
 * authorization and the URL is the object store's rather than the gateway's. ADR 0025 section 6:
 * _"the object bytes do not pass through the gateway"_.
 */
async function sendUpload(input: {
  readonly item: QueuedUpload;
  readonly api: DriverApi;
  readonly tenantId: string;
  readonly bytes: (id: string) => Blob | undefined;
  /** Absent when the token carries no `driver_id`; the attach is then skipped. */
  readonly driverId?: string | undefined;
  /** Whether the token can spend `driver-document:write` at all. */
  readonly attachable: boolean;
}): Promise<{
  /** The record as it now stands, progress included, whether or not the pass finished. */
  readonly item: QueuedUpload;
  readonly complete: boolean;
  readonly held?: true;
  /** Returned rather than thrown, so the caller can keep `item` and read this. */
  readonly error?: unknown;
}> {
  let item = input.item;

  /*
   * The initiation is re-sent for a resumed item whose bytes never reached the object store, and
   * that is not a second upload: with the same key it replays, and ADR 0025 publishes `replay` on
   * the result precisely so a client can tell. It is re-sent rather than polled because the write
   * grant is short-lived and `GET …/{uploadId}` does not carry one.
   */
  if (item.uploadId === undefined || item.written !== true) {
    const body = input.bytes(item.id);
    if (body === undefined) return { item, complete: false, held: true };
    try {
      const minted = await input.api.initiateUpload({
        tenantId: input.tenantId,
        idempotencyKey: `upload-${item.id}`,
        body: {
          contentType: item.contentType,
          expectedSizeBytes: item.expectedSizeBytes,
          expectedSha256: item.expectedSha256,
        },
      });
      // Recorded before the write is attempted: an upload that was minted and then lost its
      // connection mid-write is still this courier's upload, and the next pass replays the same key.
      item = { ...item, uploadId: minted.upload.uploadId, version: minted.upload.version };
      await input.api.writeObject({ grant: minted.grant, body, contentType: item.contentType });
      item = { ...item, written: true };
    } catch (error: unknown) {
      return { item, complete: false, error };
    }
  }

  const uploadId = item.uploadId;
  const version = item.version;
  if (uploadId === undefined || version === undefined) return { item, complete: false };

  try {
    const finished = await input.api.completeUpload({
      tenantId: input.tenantId,
      uploadId,
      idempotencyKey: `complete-${item.id}`,
      expectedVersion: version,
    });

    /*
     * **The attach, which is what makes the upload count.**
     *
     * Bytes in the object store are not a document. A permit is a DRV-03 record, EXP-003's
     * readiness gate blocks a courier on `no_documents`, and until a screen spent
     * `driver-document:write` no client in the realm held it — so every courier's upload went
     * into FIL-02 and stopped there, and nobody could enter the dispatch pool.
     *
     * Attached here rather than on the screen because this is the step that already survives a
     * reload: the queued item's own id is the `documentId`, so a replayed flush revises the same
     * document instead of creating a second one for the same photograph, and `expectedVersion`
     * stays 0 because DRV-03 compares `current?.version ?? 0` and a replay through its own
     * idempotency store never reaches the comparison.
     *
     * **Its failure does not fail the upload.** The bytes have reached the store and the
     * `complete` has been accepted; those are not undone by a 403 on the attach, and reporting
     * the item as unsent would make the next pass re-upload bytes that are already there. So the
     * item is complete either way and the attach is attempted once per completion.
     */
    if (input.attachable && input.driverId !== undefined) {
      try {
        await input.api.attachDocument({
          driverId: input.driverId,
          documentId: item.id,
          type: DESIGNED_DOCUMENT_TYPE,
          objectReference: finished.objectReference,
          evidenceSha256: finished.expectedSha256,
          expectedVersion: 0,
          idempotencyKey: `attach-${item.id}`,
        });
      } catch {
        /* Deliberately swallowed, for the reason above. */
      }
    }

    return { item: { ...item, version: finished.version }, complete: true };
  } catch (error: unknown) {
    return { item, complete: false, error };
  }
}

/**
 * What a refusal means for the queue.
 *
 * Exhaustive over the platform's eight error categories rather than defaulted, for the reason
 * `view-state.ts` gives: a category added to the runtime should fail the build here instead of being
 * rendered as a generic failure. Two of the eight are read differently from everywhere else in this
 * application, and both readings come from the contracts rather than from convenience:
 *
 *   - **conflict** is LOG-06's sequence gap, which is the one thing the design promises to say.
 *     FIL-02 also answers 409 for an idempotency clash, which is a different fact with the same
 *     status, so the code is carried through rather than swallowed.
 *   - **not_found** on FIL-02's public surface means _this upload is not yours_, per ADR 0025
 *     section 3. There is nothing a courier can do about it and nothing to report; the queue starts
 *     that item over, which is why it is reported as the ordinary queued state rather than an error.
 */
function conditionFor(error: unknown, label: string, scope: string): QueueCondition {
  if (error instanceof TransportError) return { kind: "queued" };
  if (!(error instanceof ApiError)) {
    return {
      kind: "refused",
      code: "unexpected_error",
      message: error instanceof Error ? error.message : "Something went wrong",
    };
  }
  switch (error.category) {
    case "authorization":
    case "authentication":
      return { kind: "forbidden", missingScopes: [scope] };
    case "not_found":
      return { kind: "queued" };
    case "conflict":
      return error.code === "idempotency_key_reused"
        ? { kind: "refused", code: error.code, message: error.message }
        : { kind: "gap", label };
    case "validation":
    case "rate_limit":
    case "dependency":
    case "internal":
      return { kind: "refused", code: error.code, message: error.message };
  }
}
